import {ChangeEvent, FC, useCallback, useEffect} from 'react';
//mui
import {Box, Typography} from '@mui/material';
//store
import {getPrivateTablesAndTicketsTiersSelector, getTablesAndTicketsTiersSelector} from '@/store/organization-tables';
import {useAppSelector} from '@/store/hooks';
//formik
import {Field, FieldArray, useFormikContext} from 'formik';
import {AddTableTicketFormValues, TableAndTicketsWithAttending} from '../../formConfiguration';
//components
import Input from '@/shared/components/Inputs/Input';
import MultipleSelect from '@/shared/components/MultipleSelect';
import Checkbox from '@/shared/components/Checkbox';
//constants
import {FIELD, FIELD_LABEL, NONE, NUMBER, PLACEHOLDER, SUBTITLE1, mealsSelectOptions} from '@/utils/constants';
//types
import {IOrganizationTicketsTiers, TicketTableAndTicketsAttendees} from '@/utils/types';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
import {getTiersTitle} from './helpers';
//styles
import {ticketsTiersStyle} from './TicketsTiers.styles';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {USER_TYPE} from '@/utils/enums';

const TicketsTiers: FC = () => {
  const {role} = useApiInstance();

  const {
    setFieldValue,
    setFieldError,
    values: {setTables, purchaser},
  } = useFormikContext<AddTableTicketFormValues>();

  const ordinaryTicketsTiers = useAppSelector(getTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const privateTicketsTiers = useAppSelector(getPrivateTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];

  const ticketsTiers =
    role === USER_TYPE.USER && privateTicketsTiers ? privateTicketsTiers : ordinaryTicketsTiers;

  const getTiersTitleHandler = useCallback(
    (tableInfoId: number) => getTiersTitle(ticketsTiers, tableInfoId),
    [ticketsTiers]
  );

  const purchaserIsNotAttendingChangeHandler = useCallback(
    (index: number, isNotAttending: boolean) => {
      setFieldValue(`setTables[${index}].${FIELD.purchaserIsNotAttending}`, isNotAttending);

      if (!isNotAttending) {
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.firstName}`, purchaser.firstName);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.lastName}`, purchaser.lastName);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.email}`, purchaser.email);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.phoneNumber}`, purchaser.phoneNumber);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.meals}`, purchaser.meals);
        setFieldError(`setTables[${index}].attendees[0].${FIELD.meals}`, undefined);
        setFieldError(`setTables[${index}].attendees[0].${FIELD.phoneNumber}`, undefined);
        setFieldError(`setTables[${index}].attendees[0].${FIELD.email}`, undefined);
        setFieldError(`setTables[${index}].attendees[0].${FIELD.lastName}`, undefined);
        setFieldError(`setTables[${index}].attendees[0].${FIELD.firstName}`, undefined);
      } else {
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.firstName}`, '');
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.lastName}`, '');
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.email}`, '');
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.phoneNumber}`, '');
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.meals}`, []);
      }
    },
    [purchaser, setFieldValue]
  );

  useEffect(() => {
    setTables.forEach((table, index) => {
      if (!table.purchaserIsNotAttending) {
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.firstName}`, purchaser.firstName);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.lastName}`, purchaser.lastName);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.email}`, purchaser.email);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.phoneNumber}`, purchaser.phoneNumber);
        setFieldValue(`setTables[${index}].attendees[0].${FIELD.meals}`, purchaser.meals);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaser]);

  return (
    <Box sx={ticketsTiersStyle.wrapper}>
      <Box sx={ticketsTiersStyle.content}>
        <FieldArray
          name={FIELD.setTables}
          render={() => (
            <>
              {setTables.map((table: TableAndTicketsWithAttending, tableIndex: number) => (
                <Box key={table.tableInfoId} sx={ticketsTiersStyle.table}>
                  <Box>
                    <Box sx={ticketsTiersStyle.checkBox}>
                      <Typography variant={SUBTITLE1}>
                        {getTiersTitleHandler(setTables[tableIndex].tableInfoId as number)}:
                      </Typography>
                      <Field
                        id={`setTables[${tableIndex}].${FIELD.purchaserIsNotAttending}`}
                        name={`setTables[${tableIndex}].${FIELD.purchaserIsNotAttending}`}
                        placeholder={PLACEHOLDER.tableNameOrCompany}
                        label={
                          role === USER_TYPE.USER
                            ? "I'm purchasing this for someone else"
                            : 'Purchaser is not attending event'
                        }
                        component={Checkbox}
                        onChange={(_: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                          purchaserIsNotAttendingChangeHandler(tableIndex, checked)
                        }
                        checked={!!table.purchaserIsNotAttending}
                      />
                    </Box>
                    <Field
                      id={`setTables[${tableIndex}].${FIELD.name}`}
                      name={`setTables[${tableIndex}].${FIELD.name}`}
                      placeholder={PLACEHOLDER.tableNameOrCompany}
                      label={setRequiredLabel(FIELD_LABEL.tableName)}
                      component={Input}
                      parentClassName={ticketsTiersStyle.tableName}
                      isArrayData
                    />
                  </Box>

                  {table.attendees.map((attendee: TicketTableAndTicketsAttendees, attendeeIndex: number) => (
                    <Box key={attendeeIndex} sx={ticketsTiersStyle.tableFields}>
                      <Box sx={ticketsTiersStyle.names}>
                        <Field
                          id={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.firstName}`}
                          name={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.firstName}`}
                          placeholder={PLACEHOLDER.firstName}
                          label={FIELD_LABEL.firstName}
                          value={attendee.firstName}
                          component={Input}
                          isArrayData
                        />
                        <Field
                          id={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.lastName}`}
                          name={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.lastName}`}
                          placeholder={PLACEHOLDER.lastName}
                          label={FIELD_LABEL.lastName}
                          value={attendee.lastName}
                          component={Input}
                          isArrayData
                        />
                      </Box>
                      <Box sx={ticketsTiersStyle.data}>
                        <Field
                          id={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.email}`}
                          name={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.email}`}
                          placeholder={PLACEHOLDER.emailEnter}
                          label={FIELD_LABEL.email}
                          value={attendee.email}
                          component={Input}
                          isArrayData
                        />
                        <Field
                          id={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.phoneNumber}`}
                          name={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.phoneNumber}`}
                          placeholder={PLACEHOLDER.phoneNumberZero}
                          label={FIELD_LABEL.phoneNumber}
                          value={attendee.phoneNumber}
                          component={Input}
                          type={NUMBER}
                          isArrayData
                        />
                        <Box sx={ticketsTiersStyle.meals}>
                          <Field
                            id={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.meals}`}
                            name={`setTables[${tableIndex}].attendees[${attendeeIndex}].${FIELD.meals}`}
                            value={attendee.meals}
                            defaultValue={NONE}
                            label={FIELD_LABEL.meals}
                            component={MultipleSelect}
                            options={mealsSelectOptions}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </>
          )}
        />
      </Box>
    </Box>
  );
};

export default TicketsTiers;
